import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

type LegalSectionContent = {
  title: string;
  content: ReactNode;
};

function LegalDocument({
  title,
  sections,
}: {
  title: string;
  sections: LegalSectionContent[];
}) {
  return (
    <main className="mx-auto w-full max-w-3xl py-10 md:py-16">
      <div className="mb-10 border-b border-border pb-8">
        <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-accent">
          Ontoligent legal
        </p>
        <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">{title}</h1>
        <p className="mt-4 text-sm text-muted">Effective date: 2026-06-25</p>
      </div>

      <div className="space-y-9 text-sm leading-7 text-muted">
        {sections.map((section, index) => (
          <section key={section.title} className="space-y-3">
            <h2 className="text-base font-semibold leading-6 text-text">
              {index + 1}. {section.title}
            </h2>
            {section.content}
          </section>
        ))}
      </div>

      <nav
        aria-label="Legal pages"
        className="mt-12 flex flex-wrap gap-x-6 gap-y-3 border-t border-border pt-6 text-sm"
      >
        <Link to="/" className="text-muted hover:text-text">
          Back to Home
        </Link>
        <Link to="/legal/privacy-policy" className="text-muted hover:text-text">
          Privacy Policy
        </Link>
        <Link to="/legal/terms-and-conditions" className="text-muted hover:text-text">
          Terms and Conditions
        </Link>
      </nav>
    </main>
  );
}

const listClassName = "list-disc space-y-2 pl-5 marker:text-accent";

export function PrivacyPolicy() {
  return (
    <LegalDocument
      title="Privacy Policy"
      sections={[
        {
          title: "Who We Are",
          content: (
            <p>
              Ontoligent is an enterprise intelligence platform for governed
              decisions, including ontology and knowledge graph engineering,
              permission-aware retrieval, and auditable AI workflows.
            </p>
          ),
        },
        {
          title: "Information We Collect",
          content: (
            <>
              <p>Depending on how you use Ontoligent, we may collect:</p>
              <ul className={listClassName}>
                <li>
                  Contact and account details you provide, such as your name,
                  email address, company, and support or business inquiry
                  information.
                </li>
                <li>
                  Workspace content you or your organization submit or connect
                  to the platform, such as documents, datasets, prompts,
                  configuration, and related records. This content may contain
                  personal information.
                </li>
                <li>
                  Website and service usage, device, and diagnostic data, such
                  as pages viewed, browser metadata, and operational or security
                  logs.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "How We Use Information",
          content: (
            <>
              <p>We use information to:</p>
              <ul className={listClassName}>
                <li>Provide, maintain, and secure Ontoligent and its features.</li>
                <li>
                  Respond to inquiries, provide support, and communicate
                  service-related information.
                </li>
                <li>
                  Operate and improve the website, platform performance, and
                  user experience.
                </li>
                <li>
                  Provide AI and connected-service features as configured by
                  you or your organization.
                </li>
                <li>
                  Comply with legal obligations, enforce our terms, and protect
                  our rights, users, and services.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Legal Bases (Where Applicable)",
          content: (
            <p>
              Where GDPR or similar laws apply, our legal bases may include
              consent, our legitimate interests, performance of a contract, and
              compliance with legal obligations, depending on the processing
              activity.
            </p>
          ),
        },
        {
          title: "Cookies and Analytics",
          content: (
            <p>
              We use Google Tag Manager, cookie-consent tooling, and related
              analytics technologies to understand website usage and improve
              performance. Your browser settings and applicable consent
              mechanisms may allow you to control certain data collection.
            </p>
          ),
        },
        {
          title: "Sharing and Disclosures",
          content: (
            <>
              <p>
                We may share information with trusted providers that support
                hosting, analytics, communications, security, and platform
                features. Information may also be processed by third-party
                services that you or your organization choose to connect or
                configure.
              </p>
              <p>
                We do not sell personal information. We may disclose information
                when required by law or when reasonably necessary to protect
                legal rights, users, or security.
              </p>
            </>
          ),
        },
        {
          title: "Data Retention",
          content: (
            <p>
              We retain information only for as long as reasonably necessary for
              the purposes described in this policy, including business
              operations, legal compliance, dispute resolution, security, and
              contract administration.
            </p>
          ),
        },
        {
          title: "Security",
          content: (
            <p>
              We apply reasonable administrative, technical, and organizational
              safeguards appropriate to the nature of the information and our
              services. No transmission or storage system can be guaranteed to
              be completely secure.
            </p>
          ),
        },
        {
          title: "International Transfers",
          content: (
            <p>
              If information is processed outside your jurisdiction, we take
              reasonable steps to ensure appropriate protections consistent
              with applicable law.
            </p>
          ),
        },
        {
          title: "Your Privacy Rights",
          content: (
            <p>
              Depending on your jurisdiction, you may have rights to access,
              correct, delete, restrict, object to, or request portability of
              personal information, as well as rights relating to certain
              analytics or marketing activities. To exercise applicable rights,
              contact us using the details below.
            </p>
          ),
        },
        {
          title: "Children's Privacy",
          content: (
            <p>
              Ontoligent is intended for business users and is not directed to
              children.
            </p>
          ),
        },
        {
          title: "Changes to This Policy",
          content: (
            <p>
              We may update this Privacy Policy from time to time. Updates will
              be posted on this page with a revised effective date.
            </p>
          ),
        },
        {
          title: "Contact",
          content: (
            <p>
              For privacy requests or questions, contact{" "}
              <a
                className="text-accent underline underline-offset-4"
                href="mailto:hello@ontoligent.io"
              >
                hello@ontoligent.io
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}

export function TermsAndConditions() {
  return (
    <LegalDocument
      title="Terms and Conditions"
      sections={[
        {
          title: "Agreement to These Terms",
          content: (
            <p>
              These Terms and Conditions govern your access to and use of the
              Ontoligent website and platform. By accessing or using the
              service, you agree to these terms. If you use Ontoligent on behalf
              of an organization, you confirm that you are authorized to accept
              these terms for that organization.
            </p>
          ),
        },
        {
          title: "The Service",
          content: (
            <p>
              Ontoligent provides enterprise knowledge architecture,
              ontology-based intelligence, retrieval, and AI workflow
              capabilities. Features may change over time, and some features
              may depend on your organization's configuration or connected
              services.
            </p>
          ),
        },
        {
          title: "Accounts and Access",
          content: (
            <p>
              You are responsible for keeping your account credentials
              confidential, maintaining accurate account information, and
              activity conducted through your account. Notify us promptly if
              you suspect unauthorized access. Your organization may manage
              access, roles, and workspace settings.
            </p>
          ),
        },
        {
          title: "Your Content and Responsibilities",
          content: (
            <p>
              You and your organization retain your rights to the data,
              materials, and other content submitted to or connected with
              Ontoligent (“Customer Content”). You are responsible for
              Customer Content, for having the rights and permissions needed to
              use it with the service, and for complying with applicable laws
              when collecting and processing it.
            </p>
          ),
        },
        {
          title: "Acceptable Use",
          content: (
            <>
              <p>You may not use Ontoligent to:</p>
              <ul className={listClassName}>
                <li>
                  Break the law, infringe another person's rights, or process
                  information without the permissions required by law.
                </li>
                <li>
                  Probe, disrupt, damage, or attempt to gain unauthorized
                  access to the service or its systems.
                </li>
                <li>
                  Introduce malicious code, interfere with service operation,
                  or circumvent access controls or usage limits.
                </li>
                <li>
                  Use generated outputs as a substitute for appropriate human
                  review in decisions requiring professional, legal, medical,
                  or other regulated judgment.
                </li>
              </ul>
            </>
          ),
        },
        {
          title: "Intellectual Property and License",
          content: (
            <p>
              Ontoligent and its software, branding, and materials are owned by
              Ontoligent or its licensors and are protected by applicable
              intellectual property laws. Subject to these terms, we grant you
              a limited, non-exclusive, non-transferable right to access and
              use the service for your internal business purposes. No rights
              are granted except those expressly stated here. These terms do
              not transfer ownership of Customer Content.
            </p>
          ),
        },
        {
          title: "AI and Generated Outputs",
          content: (
            <p>
              AI-generated or retrieved outputs may be incomplete, inaccurate,
              or out of date and should be reviewed before use. You are
              responsible for evaluating outputs and decisions made using the
              service. Ontoligent does not guarantee that any output is
              accurate, suitable, or legally compliant for a particular
              purpose.
            </p>
          ),
        },
        {
          title: "Third-Party Services",
          content: (
            <p>
              Ontoligent may integrate with third-party products or services.
              Your use of those services is subject to their own terms and
              policies. We are not responsible for third-party services or
              content that we do not control.
            </p>
          ),
        },
        {
          title: "Availability and Changes",
          content: (
            <p>
              We aim to provide a reliable service but do not guarantee
              uninterrupted or error-free operation. We may update, modify, or
              discontinue features. Where practicable, we will provide notice
              of material changes to the service.
            </p>
          ),
        },
        {
          title: "Suspension and Termination",
          content: (
            <p>
              We may suspend or restrict access where reasonably necessary to
              protect the service, users, or systems; address a breach of these
              terms; or comply with law. You may stop using the service at any
              time. Any separate agreement with your organization may govern
              account closure and Customer Content export or deletion.
            </p>
          ),
        },
        {
          title: "Disclaimers and Limitation of Liability",
          content: (
            <p>
              To the fullest extent permitted by law, the service is provided
              “as is” and “as available,” without warranties not expressly
              stated in these terms. To the fullest extent permitted by law,
              Ontoligent will not be liable for indirect, incidental, special,
              consequential, or punitive damages, or loss of profits, data, or
              goodwill arising from use of the service. Nothing in these terms
              limits liability that cannot lawfully be limited or excluded.
            </p>
          ),
        },
        {
          title: "Changes to These Terms",
          content: (
            <p>
              We may update these terms from time to time. We will post the
              updated terms on this page and revise the effective date.
              Continued use of the service after an update takes effect means
              you accept the updated terms, to the extent permitted by law.
            </p>
          ),
        },
        {
          title: "Contact",
          content: (
            <p>
              For questions about these terms, contact{" "}
              <a
                className="text-accent underline underline-offset-4"
                href="mailto:hello@ontoligent.io"
              >
                hello@ontoligent.io
              </a>
              .
            </p>
          ),
        },
      ]}
    />
  );
}
